import React, { ReactElement, useEffect } from "react";
import portraits, { defaultPortrait } from "../assets";
import "./KickDialog.css";

type KickConfirmProps = {
  name: string;
  /** True during a game: a bot takes over the seat. In the lobby the seat is simply freed. */
  inGame: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

function useEscape(onEscape: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onEscape]);
}

/** Asks the host to confirm before a player is removed. */
export function KickConfirm(props: KickConfirmProps): ReactElement {
  useEscape(props.onCancel);
  return (
    <div className="kd-layer kd-layer-top">
      <div className="kd-backdrop" onClick={props.onCancel} />
      <div className="kd-panel" role="alertdialog" aria-modal="true" aria-labelledby="kd-confirm-title">
        <h2 id="kd-confirm-title" className="kd-title">
          Remove {props.name}?
        </h2>
        <p className="kd-text">
          {props.inGame
            ? "A bot takes over their seat so the game can go on, and they cannot rejoin this game."
            : "They are taken out of the lobby. They can join again with the lobby code."}
        </p>
        <div className="kd-buttons">
          <button className="kd-cancel" onClick={props.onCancel} autoFocus>
            Cancel
          </button>
          <button className="kd-remove" onClick={props.onConfirm}>
            Remove {props.name}
          </button>
        </div>
      </div>
    </div>
  );
}

type KickPickerProps = {
  players: string[];
  icons: Record<string, string>;
  onPick: (name: string) => void;
  onClose: () => void;
};

/** The host's list of players who can be removed during a game. */
export function KickPicker(props: KickPickerProps): ReactElement {
  useEscape(props.onClose);
  return (
    <div className="kd-layer">
      <div className="kd-backdrop" onClick={props.onClose} />
      <div className="kd-panel" role="dialog" aria-modal="true" aria-labelledby="kd-picker-title">
        <div className="kd-head">
          <h2 id="kd-picker-title" className="kd-title">
            Remove a player
          </h2>
          <button className="kd-close" aria-label="Close" onClick={props.onClose}>
            ×
          </button>
        </div>
        {props.players.length === 0 ? (
          <p className="kd-text">There is nobody else to remove.</p>
        ) : (
          <ul className="kd-list">
            {props.players.map((name) => {
              const icon = props.icons[name];
              const portrait = icon && icon !== defaultPortrait ? portraits[icon] : undefined;
              return (
                <li key={name} className="kd-row">
                  {portrait ? (
                    <img className="kd-avatar" src={portrait} alt="" />
                  ) : (
                    <span className="kd-avatar kd-avatar-empty">{name.charAt(0)}</span>
                  )}
                  <span className="kd-name">{name}</span>
                  <button className="kd-remove kd-remove-small" onClick={() => props.onPick(name)}>
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
